// node_modules
import { FC, useCallback, useContext, useEffect, useState } from "react";
// Custom hooks
import { useDebounce, useFetch } from "Hooks";
// Types
import { TConcept, TNotificationMessageDTO, TTechnology, TUseFetch } from "Types";
// Components
import { AsynchronousWrapper, EditableMarkdown } from "Components";
// Controllers
import { TechnologyControllerSingleton } from "Controllers";
// Contexts
import { CaseContext, AuthContext } from "Stores";
import { WebsocketContext } from "Providers";
// Constants
import { HubConstants, LoggingConstants } from "Constants";
import { PendoHelperSingleton, ToastHelperSingleton } from "Helpers";

type TTechnologyOrConceptDescription = {
    typeName: string,
    typeId: number,
    originalId?: number
};

export const TechnologyOrConceptDescription: FC<TTechnologyOrConceptDescription> = ({ typeName, typeId, originalId }: TTechnologyOrConceptDescription) => {
    // State
    const [description, setDescription] = useState<string | undefined>(undefined);
    const [entityId, setEntityId] = useState<number>(typeId);

    // Let
    const debouncedDescription = useDebounce(description, 500);

    // Context
    const { caseState } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);
    const { webSocketController } = useContext(WebsocketContext);
    
    // We use `useFetch` custom hook here
    // to fetch data from the server
    const { isLoading, didError, errorMessage, fetchedData }: TUseFetch<TTechnology | TConcept> = useFetch(`api/${typeName}/${typeId}/description`);
    const technologyNameResourceName = caseState.selectedTechnologyId ? `${HubConstants.TECHNOLOGY_DESCRIPTION_RESOURCE_NAME_PREFIX}${caseState.selectedTechnologyId}` : "";

    // Logic
    const updateDescription = useCallback(async (text: string) => {
        if (text === null || text === undefined || description === null || description === undefined) return;
        setDescription(undefined);
        await TechnologyControllerSingleton.updateTechnology(typeId, true, undefined, text);
    }, [description, typeId]);

    useEffect(() => {
        setDescription(undefined);
        setEntityId(typeId);
    }, [typeId]);

    useEffect(() => {
        // do not trigger edit at first render, when switching to a new technology
        // or when displaying a new technology
        if (typeId !== entityId || 
            isLoading || 
            didError || 
            errorMessage || 
            debouncedDescription === null || 
            debouncedDescription === undefined) return;

        // send new description to the server
        updateDescription(debouncedDescription);
    }, [debouncedDescription, didError, entityId, errorMessage, isLoading, updateDescription, typeId]);

    // Logging
    useEffect(() => {
        // Log getting the description
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyGetDescription`, {
            typeName: typeName,
            typeId: typeId
        });
    }, [typeId, typeName]);

    const handleTechnologyDescriptionUpdated = useCallback((technologyDescriptionUpdated: string) => {
        // update current technology description
        if (fetchedData) fetchedData.description = technologyDescriptionUpdated;
    }, [fetchedData]);

    const handleNotificationMessages = useCallback((notificationMessage: TNotificationMessageDTO): void => {
        ToastHelperSingleton.handleNotificationMessage(notificationMessage, authState.userEmail);
    }, [authState]);

    // manage web sockets related events
    useEffect(() => {
        // safety-check
        if (!technologyNameResourceName) return;
        
        // we add two handlers to listen on update and notification messages related to current technology description resource name
        webSocketController.addHandler(`${HubConstants.UPDATE_PREFIX}${technologyNameResourceName}`, 
            handleTechnologyDescriptionUpdated);
        webSocketController.addHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${technologyNameResourceName}`, 
            handleNotificationMessages);
        
        // remove handlers on clean up time
        return () => {
            webSocketController.removeHandler(`${HubConstants.UPDATE_PREFIX}${technologyNameResourceName}`, 
                handleTechnologyDescriptionUpdated);
            webSocketController.removeHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${technologyNameResourceName}`, 
                handleNotificationMessages);
        };
    }, [handleNotificationMessages, handleTechnologyDescriptionUpdated, technologyNameResourceName, webSocketController]);

    return (
        <AsynchronousWrapper didError={didError} errorMessage={errorMessage} isLoading={isLoading} LoaderComponent={null}>
            {fetchedData && ((fetchedData.description && fetchedData.description.length > 0) || (authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery)) ?
                <div style={{marginBottom: "2rem"}}>
                    <h5>Description</h5>
                    <EditableMarkdown 
                        source={!fetchedData || !fetchedData.description ? "" : fetchedData.description} 
                        entityId={entityId.toString()}
                        isEditable={authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery && typeName.toLowerCase() === "technologies"} 
                        env={typeName.toLowerCase() === "technologies" ? {"technologyId": typeId} : undefined}
                        handleSourceEdited={setDescription} 
                        resourceName={typeName + (originalId ? originalId?.toString() : typeId) }/>
                </div>
                : <></> }
        </AsynchronousWrapper>
    );
};